import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "./Header.css";

import LogoImage from "../../../assets/images/logo.png";

import CartIcon from "../../../assets/icons/CartIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import UserIcon from "../../../assets/icons/UserIcon";
import HeartIcon from "../../../assets/icons/HeartIcon";
import HamburgerIcon from "../../../assets/icons/HamburgerIcon";
import MobileBottomNav from "../MobileBottomNav/MobileBottomNav";
import Navbar from "../Navbar/Navbar";

import { searchProducts } from "../../../services/search.service";

const ZapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);

const ChargerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 7V4a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v3" />
    <path d="M6 11h12" />
    <path d="M6 15h12" />
    <path d="M10 11v10" />
    <path d="M14 11v10" />
  </svg>
);

const BatteryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="16" height="10" rx="2" ry="2"></rect>
    <line x1="22" y1="11" x2="22" y2="13"></line>
  </svg>
);

const LeafIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
  </svg>
);

const BikeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5.5" cy="17.5" r="3.5"></circle>
    <circle cx="18.5" cy="17.5" r="3.5"></circle>
    <path d="M15 6a3.5 3.5 0 0 0-3.5 3.5V14"></path>
    <path d="M12 9.5H8.5"></path>
    <path d="M4.5 9.5H3l2.5-5h2.5"></path>
  </svg>
);

const CarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H9.3a2 2 0 0 0-1.6.8L5 11l-5.16.86a1 1 0 0 0-.84.99V16h3m14 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 16a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"></path>
  </svg>
);

const mobileCategories = [
  {
    name: "All Products",
    categoryId: "all",
    icon: <ZapIcon />,
    color: "#eab308",
    bgColor: "#fef08a",
    badge: "🔥 TRENDING",
  },
  {
    name: "Smart Lithium Ion Batteries",
    categoryId: "smart-li-ion-batteries",
    icon: <BatteryIcon />,
    color: "#3b82f6",
    bgColor: "#dbeafe",
  },
  {
    name: "Smart Lithium Phosphate Battery",
    categoryId: "smart-lithium-phosphate-battery",
    icon: <LeafIcon />,
    color: "#22c55e",
    bgColor: "#dcfce7",
  },
  {
    name: "Scooty Batteries",
    categoryId: "scooty-batteries",
    icon: <BikeIcon />,
    color: "#a855f7",
    bgColor: "#f3e8ff",
  },
  {
    name: "Smart Integrated Inverters",
    categoryId: "smart-integrated-inverters",
    icon: <ZapIcon />,
    color: "#f97316",
    bgColor: "#ffedd5",
  },
  {
    name: "Bike Batteries",
    categoryId: "bike-batteries",
    icon: <BikeIcon />,
    color: "#ec4899",
    bgColor: "#fce7f3",
  },
  {
    name: "Car Batteries",
    categoryId: "car-batteries",
    icon: <CarIcon />,
    color: "#ef4444",
    bgColor: "#fee2e2",
  },
  {
    name: "battery-chargers",
    categoryId: "battery-chargers",
    icon: <ChargerIcon />,
    color: "#06b6d4",
    bgColor: "#ecfeff",
  },
];

const Header = ({
  onNavigate,
  toUrlFriendly,
  cartItemCount,
  currentPage,
  favItemCount,
}) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [touchStartX, setTouchStartX] = useState(0);

  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const userDisplayName =
    user?.displayName?.split(" ")[0] || user?.email?.split("@")[0] || "My Account";

  const [placeholder, setPlaceholder] = useState("");
  const timeoutRef = useRef(null);
  const placeholderTexts = useRef([
    "Buy inverter...",
    "Try our batteries...",
    "Search for BMS...",
    "Find connectors...",
  ]);
  const textIndex = useRef(0);
  const charIndex = useRef(0);
  const isDeleting = useRef(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".header");
      if (!header) return;
      if (window.scrollY > 10) {
        header.classList.add("sticky");
      } else {
        header.classList.remove("sticky");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isDesktop =
        searchRef.current && searchRef.current.contains(e.target);
      const isMobile =
        mobileSearchRef.current && mobileSearchRef.current.contains(e.target);

      if (!isDesktop && !isMobile) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    const delay = setTimeout(async () => {
      try {
        const data = await searchProducts(searchQuery);

        if (data && data.length > 0) {
          setSearchResults(data);
          setShowSearchDropdown(true);
        } else {
          setSearchResults([]);
          setShowSearchDropdown(false);
        }
      } catch (e) {
        console.error("Search failed", e);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchQuery]);

  useEffect(() => {
    const stopAnimation = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    if (isMobileMenuOpen || isSearching) {
      stopAnimation();
      setPlaceholder("Search for products...");
      return;
    }

    const type = () => {
      const currentText = placeholderTexts.current[textIndex.current];
      const typingSpeed = isDeleting.current ? 80 : 120;
      let displayedText = isDeleting.current
        ? currentText.substring(0, charIndex.current - 1)
        : currentText.substring(0, charIndex.current + 1);

      setPlaceholder(displayedText + "|");

      if (isDeleting.current) {
        charIndex.current--;
      } else {
        charIndex.current++;
      }

      if (!isDeleting.current && charIndex.current > currentText.length) {
        isDeleting.current = true;
        timeoutRef.current = setTimeout(type, 2000);
      } else if (isDeleting.current && charIndex.current === 0) {
        isDeleting.current = false;
        textIndex.current =
          (textIndex.current + 1) % placeholderTexts.current.length;
        timeoutRef.current = setTimeout(type, 500);
      } else {
        timeoutRef.current = setTimeout(type, typingSpeed);
      }
    };

    timeoutRef.current = setTimeout(type, 250);
    return stopAnimation;
  }, [isMobileMenuOpen, isSearching]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
  }, [isMobileMenuOpen]);

  const renderDropdownItem = (item) => (
    <div
      key={item.id}
      className="search-item"
      onClick={() => {
        onNavigate(`product/${item.id}`);
        setSearchQuery("");
        setShowSearchDropdown(false);
        setMobileMenuOpen(false);
      }}
    >
      <img src={item.image} alt={item.name} />

      <div className="search-info">
        <h4>{item.name}</h4>
        <div className="search-meta">
          <span className={`stock-status ${item.stock <= 0 ? "out" : ""}`}>
            {item.stock > 0 ? `${item.stock} in Stock` : "Out of Stock"}
          </span>
          <span style={{ fontSize: "11px", color: "#999" }}>
            {" "}
            | {item.category || "General"}
          </span>
        </div>
      </div>

      <div className="search-price-block">
        {item.oldPrice && (
          <span className="search-old-price">₹{item.oldPrice}</span>
        )}
        <span className="search-price">₹{item.price}</span>
      </div>
    </div>
  );

  const renderMobileNav = () => (
    <div
      className={`mobile-nav-overlay ${isMobileMenuOpen ? "open" : ""}`}
      onClick={() => setMobileMenuOpen(false)}
    >
      <div
        className="mobile-nav-content app-style-drawer"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
        onTouchMove={(e) => {
          const touchEndX = e.touches[0].clientX;
          if (touchStartX - touchEndX > 80) {
            setMobileMenuOpen(false);
          }
        }}
      >
        <div className="mobile-nav-header">
          <h3>Explore</h3>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="close-btn"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="mobile-nav-links">
          <h4 className="mobile-nav-title">Categories</h4>

          <div className="category-list-container">
            {mobileCategories.map((cat) => (
              <button
                key={cat.name}
                className="modern-category-btn"
                onClick={() => {
                  if (cat.categoryId === "all") {
                    onNavigate("shop");
                  } else {
                    onNavigate(`shop?category=${cat.categoryId}`);
                  }
                  setMobileMenuOpen(false);
                }}
              >
                <div
                  className="cat-icon-box"
                  style={{ color: cat.color, backgroundColor: cat.bgColor }}
                >
                  {cat.icon}
                </div>
                <span className="cat-text">{cat.name}</span>
                {cat.badge && <span className="cat-badge">{cat.badge}</span>}
              </button>
            ))}
          </div>

          <hr className="nav-divider" />

          <h4 className="mobile-nav-title">More</h4>

          <div className="category-list-container">
            <button
              className="modern-category-btn plain-btn"
              onClick={() => {
                onNavigate("check-warranty");
                setMobileMenuOpen(false);
              }}
            >
              <div className="cat-icon-box plain-box">🛡️</div>
              <span className="cat-text">Check Warranty</span>
            </button>

            <button
              className="modern-category-btn plain-btn"
              onClick={() => {
                onNavigate("about-us");
                setMobileMenuOpen(false);
              }}
            >
              <div className="cat-icon-box plain-box">ℹ️</div>
              <span className="cat-text">About Us</span>
            </button>

            <button
              className="modern-category-btn plain-btn"
              onClick={() => {
                onNavigate("contact");
                setMobileMenuOpen(false);
              }}
            >
              <div className="cat-icon-box plain-box">📞</div>
              <span className="cat-text">Contact Us</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );

  return (
    <header className="header">
      {currentPage === "auth" && (
        <style>
          {`
            @media (max-width: 991px) {
              .support-call, .mobile-search-container {
                display: none !important;
              }
            }
          `}
        </style>
      )}

      {renderMobileNav()}

      <div className="header-content-wrapper">
        <div className="header-top">
          <div className="logo-container">
            <a
              href="#home"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("home");
              }}
            >
              <img
                src={LogoImage}
                alt="Zynventics Logo"
                className="logo-image"
              />
              <h1>Zynventics</h1>
            </a>
          </div>

          <div
            className="search-bar"
            ref={searchRef}
            style={{ position: "relative" }}
          >
            <input
              type="text"
              placeholder="Search for Batteries, Inverters, BMS..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearchDropdown(true)}
            />
            <button>
              <SearchIcon />
            </button>

            {showSearchDropdown && searchResults.length > 0 && (
              <div className="search-dropdown">
                <div className="search-dropdown-header">
                  <span>Products</span>
                  <span>{searchResults.length} items found</span>
                </div>
                <div className="search-results-list">
                  {searchResults.map((item) => renderDropdownItem(item))}
                </div>
                <div
                  className="search-footer"
                  onClick={() => onNavigate("shop")}
                >
                  View all results →
                </div>
              </div>
            )}
          </div>

          <div className="mobile-header-actions">
            <button
              className="action-item-mobile wishlist-mobile-btn"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("favourite");
              }}
              aria-label="Favorites"
            >
              <HeartIcon />
              {favItemCount > 0 && (
                <span className="mobile-badge">{favItemCount}</span>
              )}
            </button>

            <button
              className="hamburger-menu"
              onClick={() => {
                setMobileMenuOpen(true);
              }}
              aria-label="Menu"
            >
              <HamburgerIcon />
            </button>
          </div>

          <div className="support-call">
            <div className="call-icon">📞</div>
            <div className="call-text">
              <span className="help-text">Need help? Call us:</span>
              <a href="tel:+918902955624" className="call-number">
                +91 8902955624
              </a>
            </div>
          </div>

          <div className="user-actions" style={{ alignItems: "flex-end" }}>
            {isAuthenticated ? (
              <a
                href="#account"
                className="action-item"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate("account");
                }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}
              >
                <div style={{ position: "relative", display: "inline-block", paddingBottom: "4px" }}>
                  <UserIcon />
                </div>
                <span>Hello, {userDisplayName}</span>
              </a>
            ) : (
              <a
                href="#auth"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate("auth");
                }}
                className="action-item"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}
              >
                <div style={{ position: "relative", display: "inline-block", paddingBottom: "4px" }}>
                  <UserIcon />
                </div>
                <span>Sign In</span>
              </a>
            )}
            
            <a
              href="#favorites"
              className="action-item"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("favourite");
              }}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}
            >
              <div style={{ position: "relative", display: "inline-block", paddingBottom: "4px" }}>
                <HeartIcon />
                {favItemCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-5px",
                      right: "-8px",
                      backgroundColor: "red",
                      color: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {favItemCount}
                  </span>
                )}
              </div>
              <span>Favorites</span>
            </a>
            
            <a
              href="#cart"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("cart");
              }}
              className="action-item"
              style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}
            >
              <div style={{ position: "relative", display: "inline-block", paddingBottom: "4px" }}>
                <CartIcon itemCount={cartItemCount} />
              </div>
              <span>My Cart</span>
            </a>
          </div>
        </div>

        <div
          className="mobile-search-container"
          ref={mobileSearchRef}
          style={{ position: "relative", zIndex: 1000 }}
        >
          <input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearching(true)}
            onBlur={() => {
              if (searchQuery === "") {
                setIsSearching(false);
              }
            }}
          />
          <button>
            <SearchIcon />
          </button>

          {showSearchDropdown && searchResults.length > 0 && (
            <div
              className="search-dropdown"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                width: "100%",
                minWidth: "100%",
                zIndex: 9999,
              }}
            >
              <div className="search-results-list">
                {searchResults.map((item) => renderDropdownItem(item))}
              </div>
              <div className="search-footer" onClick={() => onNavigate("shop")}>
                View all results →
              </div>
            </div>
          )}
        </div>
      </div>
      <Navbar
        onNavigate={onNavigate}
        toUrlFriendly={toUrlFriendly}
        currentPage={currentPage}
      />
      <MobileBottomNav
        onNavigate={onNavigate}
        cartItemCount={cartItemCount}
        currentPage={currentPage}
        isMobileMenuOpen={isMobileMenuOpen}
      />
    </header>
  );
};

export default Header;